// PubSubDebugger.tsx
import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { getMultiHubSocketService } from '../../utils/MultiHubSocketService';

const FIXED_CHAT_ID = '40';
const GROUP_NAME = `chat_${FIXED_CHAT_ID}`;
const HUB_NAME = 'messages';

const PubSubDebugger: React.FC = () => {
  const [logs, setLogs] = useState<string[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [userId, setUserId] = useState('debug_user_001');
  const [messageText, setMessageText] = useState('');
  const [readMessageId, setReadMessageId] = useState('');
  const [typing, setTyping] = useState(false);

  const addLog = (msg: string) => {
    setLogs(prev => [
      ...prev,
      `[${new Date().toISOString()}] ${msg}`,
    ]);
  };

  useEffect(() => {
    const service = getMultiHubSocketService();

    // Let your negotiation code know who we are
    service.setCurrentUserId(userId);

    const connectAndWire = async () => {
      addLog(`Connecting to "${HUB_NAME}" hub as user "${userId}"...`);

      try {
        const ok = await service.connectToHub(HUB_NAME);
        if (!ok) {
          throw new Error('connectToHub returned false');
        }

        setIsConnected(true);
        addLog(`[OK] Connected to "${HUB_NAME}" hub!`);

        // Clean any old listeners on this hub
        service.removeAllListeners(HUB_NAME);

        // --- RAW socket-level listener (for low-level debugging) ---
        const socket = (service as any).sockets?.get(HUB_NAME)?.socket;
        if (socket) {
          socket.off('message'); // just in case
          socket.on('message', (raw: any) => {
            addLog(`RAW SOCKET "message": ${JSON.stringify(raw)}`);
          });
          addLog('Attached low-level socket.on("message") listener');
        } else {
          addLog('[WARNING] No underlying socket found for hub; check MultiHubSocketService.sockets map');
        }

        // --- High-level hub events (your Web PubSub events) ---

        // Group/broadcast chat messages
        service.on(HUB_NAME, 'message', (data: any) => {
          addLog(`EVENT message → ${JSON.stringify(data)}`);
        });

        // Delivery/ACK from backend to group
        service.on(HUB_NAME, 'message_received', (data: any) => {
          addLog(`EVENT message_received → ${JSON.stringify(data)}`);
        });

        // Someone joined the chat
        service.on(HUB_NAME, 'user_joined', (data: any) => {
          addLog(`EVENT user_joined → ${JSON.stringify(data)}`);
        });

        // Typing indicators
        service.on(HUB_NAME, 'typing', (data: any) => {
          addLog(`EVENT typing → ${JSON.stringify(data)}`);
        });

        service.on(HUB_NAME, 'stop_typing', (data: any) => {
          addLog(`EVENT stop_typing → ${JSON.stringify(data)}`);
        });

        // Message read receipts
        service.on(HUB_NAME, 'message_read', (data: any) => {
          addLog(`EVENT message_read → ${JSON.stringify(data)}`);
        });

        // Per-message ACK back to a user (from handle_send_message_event)
        service.on(HUB_NAME, 'message_sent_*', (data: any, eventName: string) => {
          addLog(`EVENT ${eventName} → ${JSON.stringify(data)}`);
        });

        // Wildcard - catches anything not explicitly wired
        service.on(HUB_NAME, '*', (data: any, eventName: string) => {
          addLog(`* WILDCARD ${eventName} → ${JSON.stringify(data)}`);
        });

        // --- Join the fixed chat group via backend join_chat handler ---
        addLog(
          `Emitting "join_chat" for chat_id=${FIXED_CHAT_ID}, expected group="${GROUP_NAME}"`,
        );
        service.emit(HUB_NAME, 'join_chat', {
          chat_id: FIXED_CHAT_ID,
          group: GROUP_NAME, // <- backend can use this to call add_connection_to_group
        });

      } catch (err: any) {
        addLog(`[ERROR] Connection failed: ${err.message || String(err)}`);
        setIsConnected(false);
      }
    };

    connectAndWire();

    return () => {
      const svc = getMultiHubSocketService();
      svc.removeAllListeners(HUB_NAME);
      svc.disconnectHub(HUB_NAME);
      addLog('Disconnected from hub and cleaned up listeners');
    };
    // Reconnect if userId changes
  }, [userId]);

  // --- UI action helpers ----------------------------------------------------------

  const handleJoinChat = () => {
    const service = getMultiHubSocketService();
    addLog(
      `Manual "join_chat" emit for chat_id=${FIXED_CHAT_ID}, group="${GROUP_NAME}"`,
    );
    service.emit(HUB_NAME, 'join_chat', {
      chat_id: FIXED_CHAT_ID,
      group: GROUP_NAME,
    });
  };

  const handleSendMessage = () => {
    if (!messageText.trim()) {
      addLog('Refusing to send empty message');
      return;
    }

    const service = getMultiHubSocketService();
    const localMessageId = uuidv4();
    const correlationId = uuidv4();

    addLog(
      `Emitting "send_message" → chat_id=${FIXED_CHAT_ID}, group="${GROUP_NAME}", localMessageId=${localMessageId}`,
    );

    service.emit(HUB_NAME, 'send_message', {
      chat_id: FIXED_CHAT_ID,
      group: GROUP_NAME,
      content: messageText.trim(),
      messageId: localMessageId,
      correlation_id: correlationId,
    });

    setMessageText('');
  };

  const handleTypingToggle = () => {
    const service = getMultiHubSocketService();
    const event = typing ? 'stop_typing' : 'typing';

    addLog(`Emitting "${event}" for chat_id=${FIXED_CHAT_ID}, group="${GROUP_NAME}"`);
    service.emit(HUB_NAME, event, {
      chat_id: FIXED_CHAT_ID,
      group: GROUP_NAME,
    });

    setTyping(!typing);
  };

  const handleMarkRead = () => {
    if (!readMessageId.trim()) {
      addLog('No message_id provided for message_read');
      return;
    }

    const service = getMultiHubSocketService();
    addLog(
      `Emitting "message_read" for chat_id=${FIXED_CHAT_ID}, group="${GROUP_NAME}", message_id=${readMessageId}`,
    );

    service.emit(HUB_NAME, 'message_read', {
      chat_id: FIXED_CHAT_ID,
      group: GROUP_NAME,
      message_id: readMessageId.trim(),
    });
  };

  return (
    <div
      style={{
        padding: 20,
        fontFamily: 'monospace',
        background: '#000',
        color: '#0f0',
        minHeight: '100vh',
      }}
    >
      <h1>Web PubSub Chat Debugger</h1>
      <p>
        Hub: <strong>{HUB_NAME}</strong> | Fixed chat_id:{' '}
        <strong>{FIXED_CHAT_ID}</strong> | Group:{' '}
        <strong>{GROUP_NAME}</strong> | Status:{' '}
        <strong style={{ color: isConnected ? 'lime' : 'red' }}>
          {isConnected ? 'CONNECTED' : 'DISCONNECTED'}
        </strong>
      </p>

      <div style={{ marginBottom: 20 }}>
        <label>
          User ID (for auth / negotiation):{' '}
          <input
            value={userId}
            onChange={e => setUserId(e.target.value)}
            style={{ padding: 8, width: 260 }}
          />
        </label>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 20,
          marginBottom: 20,
        }}
      >
        {/* Chat controls */}
        <div
          style={{
            background: '#111',
            padding: 12,
            border: '1px solid #0f0',
            borderRadius: 4,
          }}
        >
          <h3>Chat Controls</h3>
          <button
            onClick={handleJoinChat}
            style={{
              padding: '6px 10px',
              marginRight: 10,
              marginBottom: 10,
              cursor: 'pointer',
            }}
          >
            Join Chat (chat_id = {FIXED_CHAT_ID}, group = {GROUP_NAME})
          </button>

          <div style={{ marginTop: 10 }}>
            <label>
              Message:{' '}
              <input
                value={messageText}
                onChange={e => setMessageText(e.target.value)}
                style={{ padding: 6, width: '100%' }}
                placeholder="Type a message to send..."
              />
            </label>
            <button
              onClick={handleSendMessage}
              style={{
                padding: '6px 10px',
                marginTop: 8,
                cursor: 'pointer',
              }}
            >
              Send Message
            </button>
          </div>

          <div style={{ marginTop: 16 }}>
            <button
              onClick={handleTypingToggle}
              style={{
                padding: '6px 10px',
                cursor: 'pointer',
              }}
            >
              {typing ? 'Send stop_typing' : 'Send typing'}
            </button>
          </div>
        </div>

        {/* Read / receipts */}
        <div
          style={{
            background: '#111',
            padding: 12,
            border: '1px solid #0f0',
            borderRadius: 4,
          }}
        >
          <h3>Read / Receipts</h3>
          <label>
            Message ID to mark as read:{' '}
            <input
              value={readMessageId}
              onChange={e => setReadMessageId(e.target.value)}
              style={{ padding: 6, width: '100%' }}
              placeholder="Paste a message_id from logs"
            />
          </label>
          <button
            onClick={handleMarkRead}
            style={{
              padding: '6px 10px',
              marginTop: 8,
              cursor: 'pointer',
            }}
          >
            Emit message_read
          </button>

          <p style={{ marginTop: 12, color: '#aaa', fontSize: 12 }}>
            Tip: use a <code>message_id</code> you see in
            <br />
            <code>message</code> / <code>message_received</code> events.
          </p>
        </div>
      </div>

      <h3>Live Event Log (scroll down)</h3>
      <pre
        style={{
          background: '#111',
          padding: 15,
          height: '55vh',
          overflowY: 'auto',
          border: '1px solid #0f0',
          whiteSpace: 'pre-wrap',
        }}
      >
        {logs.join('\n')}
      </pre>

      <details style={{ marginTop: 20, color: '#aaa' }}>
        <summary>How to use</summary>
        <ul>
          <li>Open this page while authenticated (so negotiation works).</li>
          <li>
            The debugger always uses <code>chat_id = {FIXED_CHAT_ID}</code> and group{' '}
            <code>{GROUP_NAME}</code>.
          </li>
          <li>
            On the backend, your <code>join_chat</code> handler should call{' '}
            <code>add_connection_to_group(group=&quot;{GROUP_NAME}&quot;,...)</code>.
          </li>
          <li>Click “Join Chat” to emit the <code>join_chat</code> event.</li>
          <li>
            Send a message and watch:
            <ul>
              <li>
                <code>send_message</code> → backend handler →
                <code>message</code> + <code>message_received</code> events
              </li>
              <li>
                Per-message ACKs via <code>message_sent_*</code> if your backend sends
                them.
              </li>
            </ul>
          </li>
          <li>Use typing / stop_typing to test typing indicators.</li>
          <li>Use message_read to test read receipts.</li>
        </ul>
      </details>
    </div>
  );
};

export default PubSubDebugger;
